<script lang="ts" setup>
import { toastServiceError } from '@/services';
import ScriptService from '@/services/api/ScriptService';
import { useUiStore } from '@/stores';
import { Permission, ScriptModel } from '@/types';
import { computed, ref, Ref } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const uiStore = useUiStore();

const JQUERY_URL = 'https://code.jquery.com/jquery-2.1.3.min.js';
const JQUERY_UI_URL = 'https://code.jquery.com/ui/1.11.2/jquery-ui.min.js';

const script: Ref<ScriptModel | undefined> = ref(undefined);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getScript = async (id: number) => {
    try {
        setLoading(true);
        const { data } = await ScriptService.get<ScriptModel>(id);
        script.value = data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

getScript(Number(route.params.id));

const scripts = computed(() => {
    let scripts = '';

    if (script.value?.includeJquery) {
        const scriptJqueryTag = document.createElement('script');
        scriptJqueryTag.setAttribute('src', JQUERY_URL);
        scripts += scriptJqueryTag.outerHTML + '\n';

        const scriptJqueryUiTag = document.createElement('script');
        scriptJqueryUiTag.setAttribute('src', JQUERY_UI_URL);
        scripts += scriptJqueryUiTag.outerHTML + '\n';
    }

    const scriptUrlTag = document.createElement('script');
    scriptUrlTag.setAttribute('src', script.value?.scriptUrl || '');
    scripts += scriptUrlTag.outerHTML;

    return scripts;
});

const copyToClipboard = () => {
    const textarea = document.createElement('textarea');
    textarea.value = scripts.value;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
};
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('script.view.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex space-x-4">
                    <router-link :to="{ name: 'ListScript' }">
                        <UiButton
                            text-variant="primary"
                            no-border
                            no-shadow
                            class="hover:bg-white/50"
                            v-tippy="$t('core.actions.Back')"
                        >
                            {{ $t('core.actions.Back') }}
                        </UiButton>
                    </router-link>
                    <router-link
                        v-if="$can(Permission.EDIT_SCRIPT)"
                        :to="{
                            name: 'EditScriptById',
                            params: {
                                id: route.params.id,
                            },
                        }"
                        class="block hover:bg-gray-50"
                    >
                        <UiButton variant="primary" text-variant="white">
                            {{ $t('core.actions.Edit') }}
                        </UiButton>
                    </router-link>
                </div>
            </template>
        </UiPageHeader>

        <div class="space-y-2">
            <UiPanel class="flex px-4 py-2">
                <div class="grid grid-cols-1 gap-6 md:grid-cols-5">
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('script.labels.clientUrl') }}</h3>
                        <p>{{ script?.clientUrl }}</p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('script.labels.includeJquery') }}</h3>
                        <p
                            :class="[
                                script?.includeJquery
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800',
                            ]"
                            class="inline-flex rounded-full px-2 text-sm font-semibold leading-5"
                        >
                            {{
                                script?.includeJquery
                                    ? $t('core.options.Active')
                                    : $t('core.options.Inactive')
                            }}
                        </p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('script.labels.isActive') }}</h3>
                        <p
                            :class="[
                                script?.isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800',
                            ]"
                            class="inline-flex rounded-full px-2 text-sm font-semibold leading-5"
                        >
                            {{
                                script?.isActive
                                    ? $t('core.options.Active')
                                    : $t('core.options.Inactive')
                            }}
                        </p>
                    </div>
                </div>
            </UiPanel>
            <UiPanel v-if="script?.isActive">
                <pre class="whitespace-pre-wrap break-words">{{ scripts }}</pre>
                <div class="flex justify-end px-4 sm:px-0">
                    <UiButton
                        variant="white"
                        text-variant="gray-500"
                        class="mt-4 overflow-auto"
                        @click="copyToClipboard"
                    >
                        {{ $t('script.actions.copyToClipboard') }}
                    </UiButton>
                </div>
            </UiPanel>
        </div>
    </div>
</template>
